// Lib
import { useEffect, useLayoutEffect } from 'react';
import * as Rematrix from 'rematrix';

// Hooks
import usePrevious from '../../../../../../../utils/react/usePrevious';

// Types
import { AiLocalAiConversation } from '../../state/aiToolStateTypes';
import { getTranslationY } from '../../../../../../../../common/maths/matrix/matrixUtils';

const RESULT_ANIMATION_DURATION = 400;
const RESULT_OPACITY_TRANSITION_DURATION = 100;

/**
 * Animates the result (and the surrounding UL) in and out when it gets added or removed from the list.
 */
const animateResultHeight = (result: HTMLLIElement, fromHeight: number, toHeight: number, duration: number): number => {
    const resultHeightDiff = toHeight - fromHeight;

    const isAnimatingIn = resultHeightDiff > 0;

    const animationDataAttribute = isAnimatingIn ? 'hasAnimatedHeight' : 'hasAnimatedOut';

    // Don't animate if the result has already been animated / is already animating
    if (result.dataset[animationDataAttribute] === 'true') return 0;

    const ancestorUl = result.closest<HTMLUListElement>('.AiAssistantPopupResultsContent ul');

    if (!ancestorUl) return 0;

    // If we're still animating the previous result, then we need to switch it up halfway through
    // So we find out how far through the current animation we are and add it to the new animation
    // We also need to reset the transition, otherwise the browser will attempt to animate to the
    // new transition, but we need it to start from that point immediately.
    const currentTransformationStyle = getComputedStyle(ancestorUl).transform;

    const transform = Rematrix.fromString(currentTransformationStyle);
    const currentTranslation = getTranslationY(transform) || 0;
    ancestorUl.style.transition = '';

    const translationDistance = currentTranslation + resultHeightDiff;

    // If we're animating in a result, this will be the starting point
    // If we're animating out, it will be the ending point
    const ulTransformationY = `calc(${Math.abs(translationDistance)}px + var(--result-margin-bottom))`;
    const ulStartY = isAnimatingIn ? ulTransformationY : 0;
    const ulEndY = isAnimatingIn ? 0 : ulTransformationY;

    // If we're animating in a result, it will start completely transparent
    // If we're animating out, it will be completely opaque
    const resultStartOpacity = isAnimatingIn ? '0' : '1';
    const resultEndOpacity = isAnimatingIn ? '1' : '0';

    ancestorUl.style.transform = `translate3d(0, ${ulStartY}, 0)`;

    // Set initial height and transition
    result.style.opacity = resultStartOpacity;
    result.style.overflow = 'hidden';
    result.dataset[animationDataAttribute] = 'true';

    // Force a reflow to ensure the transition is applied
    requestAnimationFrame(() => {
        if (!result || !ancestorUl) return;

        ancestorUl.style.transform = `translate3d(0, ${ulEndY}, 0)`;
        ancestorUl.style.transition = `transform ${duration}ms ease-in-out`;

        result.style.opacity = resultEndOpacity;
        result.style.transition = `opacity ${RESULT_OPACITY_TRANSITION_DURATION}ms linear`;

        if (!isAnimatingIn) {
            result.style.transitionDelay = `${duration - RESULT_OPACITY_TRANSITION_DURATION}ms`;
        }
    });

    // Remove the transition and height after the animation is complete
    setTimeout(() => {
        requestAnimationFrame(() => {
            ancestorUl.style.transform = '';
            ancestorUl.style.transition = '';

            // If we're animating it out, then we need to remove it when it's finished the animation
            // We can't remove it from the DOM though because the CSSTransitionGroup needs to handle that
            // So instead we just hide it and make it take up the extra margin space
            if (!isAnimatingIn) {
                result.style.visibility = 'hidden';
                result.style.height = 'calc(var(--result-margin-bottom-last) - var(--result-margin-bottom))';
            }

            result.style.transition = '';
            result.style.overflow = '';
            result.style.opacity = '';
        });
    }, duration);

    return duration;
};

// Scroll to the bottom of the results container
const forceScrollToBottom = (container: HTMLDivElement | null): void => {
    if (!container) return;
    container.scrollTop = container.scrollHeight;
};

// Repeatedly scroll to the bottom of the results container each frame for the specified duration
const forceScrollToBottomForDuration = (container: HTMLDivElement | null, duration: number): void => {
    let shouldScrollToBottom = true;

    const forceScrollToBottomRepeat = (container: HTMLDivElement | null): void => {
        forceScrollToBottom(container);

        if (shouldScrollToBottom) {
            requestAnimationFrame(() => forceScrollToBottomRepeat(container));
        }
    };

    forceScrollToBottomRepeat(container);

    setTimeout(() => {
        shouldScrollToBottom = false;
    }, duration);
};

/**
 * Maintains the scroll position of the results container.
 * Contains 3 effects:
 * 1. Instantly scroll to the bottom of the results container when the conversation changes
 * 2. Smooth scroll to the bottom of the results container when a new result is added
 * 3. Smooth scroll to the bottom of the results container when streaming results are finished
 */
const useUpdateResultsScrollTop = (
    containerRef: React.RefObject<HTMLDivElement | null>,
    currentConversation: AiLocalAiConversation,
): void => {
    const conversationId = currentConversation?._id;
    const prevConversationId = usePrevious(conversationId);

    const fetching = currentConversation?.fetching || false;
    const prevFetching = usePrevious(fetching);

    const results = currentConversation?.results || [];
    const resultsLength = results.length || 0;
    const prevResultLength = usePrevious(resultsLength);

    // NOTE: We need to do this in a useEffect rather than useLayoutEffect, otherwise the CSS transition
    //  won't work correctly when switching conversations
    useEffect(() => {
        /*
        Effect 1: Instant scroll to the bottom of the results container when the conversation changes,
        or when the conversation is finished fetching
        */
        if (conversationId !== prevConversationId || (prevFetching && !fetching)) {
            forceScrollToBottom(containerRef.current);
            return;
        }
    }, [conversationId, prevConversationId, fetching, prevFetching]);

    useLayoutEffect(() => {
        // Don't allow Effect 2 to happen if we're switching conversations
        if (conversationId !== prevConversationId || (prevFetching && !fetching)) return;

        /*
        Effect 2: Animate in or out a result when it's added to or removed from the conversation
                  Maintain scroll position to bottom of results container for the duration of the animation
        NOTE: This shouldn't happen for the first result because it will animate in via the CSS transition
        */
        if (resultsLength !== prevResultLength && prevResultLength !== 0) {
            const lastResult = containerRef.current?.querySelector('ul > span > li:last-child') as HTMLLIElement;
            if (!lastResult) return;

            // If the list is growing we're going from 0 to the height of the result
            // Otherwise we're going from the height of the result to 0
            const fromHeight = resultsLength > prevResultLength ? 0 : lastResult.scrollHeight;
            const toHeight = resultsLength > prevResultLength ? lastResult.scrollHeight : 0;

            const duration = animateResultHeight(lastResult, fromHeight, toHeight, RESULT_ANIMATION_DURATION);
            forceScrollToBottomForDuration(containerRef.current, duration);
        }
    }, [conversationId, prevConversationId, fetching, prevFetching, resultsLength, prevResultLength]);

    /*
    Effect 3: When streaming finishes,
    maintain scroll position to bottom of results container for the duration of the form slide animation
    */
    const streamingResults = currentConversation?.streamingResults || false;
    const prevStreamingResults = usePrevious(currentConversation?.streamingResults);
    useLayoutEffect(() => {
        if (prevStreamingResults && !streamingResults) {
            forceScrollToBottomForDuration(containerRef.current, 600);
        }
    }, [streamingResults, prevStreamingResults]);
};

export default useUpdateResultsScrollTop;
